import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, CheckCircle2Icon, LockIcon, PrinterIcon, SmartphoneIcon, ZapIcon } from 'lucide-react';
import { CTAButton } from './Shared';
import { BodySilhouette, DnaHelix, LeafSprig, Mandala, TreeOfLife } from './BioDecor';
const productImage = 'https://cdn.magicpatterns.com/uploads/5VFCV6oCSudmsxYWXNQ9nq/ChatGPT_Image_4_sept_2026,_14_25_51.png';
const stats = [{
  icon: SmartphoneIcon,
  text: 'Úsalo desde celular, tablet o computadora'
}, {
  icon: ZapIcon,
  text: 'Acceso inmediato · 100% digital'
}, {
  icon: LockIcon,
  text: 'Pago seguro · Pago único'
}];
export function Hero() {
  return <header className="relative overflow-hidden bg-brand-blueDeep pb-8 pt-5 text-brand-cream md:pb-20 md:pt-16" data-id="element-137" data-mp="sde28rx51jv8">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden" data-id="element-138" data-mp="nekl8pin7aqb">
        <TreeOfLife className="animate-float-slow absolute -left-8 top-10 h-72 w-44 text-brand-greenSoft/30 md:h-96 md:w-56" data-id="element-139" data-mp="3cz5v8bjvq00c" />
        <LeafSprig className="animate-float-soft absolute -right-8 top-24 h-72 w-44 -scale-x-100 text-brand-blueSoft/25 md:h-[26rem] md:w-56" data-id="element-140" data-mp="3j7zg1kyg9whq" />
        <DnaHelix className="animate-float-soft absolute left-[8%] top-[48%] hidden h-56 w-24 text-brand-goldLight/25 lg:block" data-id="element-141" data-mp="3o73p8bldju82" />
        <BodySilhouette className="animate-float-slow absolute bottom-8 right-[7%] hidden h-56 w-28 text-brand-blueSoft/25 lg:block" data-id="element-142" data-mp="39cpfz2rm3z6l" />
        <Mandala className="absolute right-[16%] top-[16%] h-14 w-14 text-brand-amber/35 md:h-20 md:w-20" data-id="element-143" data-mp="30hrf99i83i4p" />
        <div className="absolute -top-24 left-1/2 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-brand-blueMid/40 blur-[120px]" data-id="element-144" data-mp="359t03jtgih6c" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-145" data-mp="2qhqwboo9n411">
        <motion.div initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-146" data-mp="2vj7uhcfd8cdx">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-brand-gold/50 bg-white/10 px-3 py-1 backdrop-blur-sm md:mb-4 md:px-4 md:py-1.5" data-id="element-147" data-mp="2lmv0edfwrwin">
            <span className="font-title text-[10px] font-bold tracking-[.18em] text-brand-goldLight md:text-xs" data-id="element-148" data-mp="najhpovn0ffj">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="mx-auto max-w-5xl" data-id="element-149" data-mp="ddww2xvhlkcg">
            <h1 className="neon-title font-serif text-[26px] font-bold leading-[1.1] tracking-tight md:text-[clamp(2.4rem,6.4vw,4.8rem)] md:leading-[1.02] md:tracking-[-0.035em]" data-id="element-150" data-mp="deyh9v67vn7w">
              <span className="block" data-id="element-151" data-mp="162c9xh2vtwm7">Sesiones más seguras,</span>
              <span className="neon-accent block" data-id="element-152" data-mp="1126mkd3naw9x">más profundas</span>
              <span className="block" data-id="element-153" data-mp="114kg2hiog0fd">y sin improvisar.</span>
            </h1>
            <div className="neon-underline mx-auto mt-3.5 w-32 md:mt-5 md:w-64" aria-hidden="true" data-id="element-154" data-mp="x0utfxlkbswz" />
            <p className="mx-auto mt-3.5 max-w-2xl text-[13px] font-medium leading-relaxed text-brand-cream/80 md:mt-5 md:text-lg" data-id="element-155" data-mp="3lquf1phvu95l">
              El conocimiento ya lo tienes. Ahora también puedes tener un sistema para organizarlo y aplicarlo con
              mayor claridad, estructura y criterio profesional.
            </p>
          </div>

          <div className="mx-auto mt-4 flex max-w-2xl justify-center md:mt-10" data-id="element-156" data-mp="269hc43xu732r">
            <motion.img src={productImage} alt="Kit Maestro de Biodescodificación: el manual principal con sus láminas de protocolos, fichas y recursos" initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1]
          }} className="h-auto w-[82%] max-w-[300px] drop-shadow-2xl sm:w-[80%] sm:max-w-[380px] md:w-[94%] md:max-w-[600px]" draggable={false} fetchPriority="high" data-id="element-157" data-mp="2b0pnh5iq8yik" />
          </div>

          <div className="mx-auto mt-4 max-w-3xl space-y-2.5 md:mt-6 md:space-y-3" data-id="element-158" data-mp="uo0svysz6ooj">
            <h2 className="font-title text-[15px] font-bold leading-snug tracking-normal text-brand-goldLight md:text-3xl md:tracking-wide" data-id="element-159" data-mp="zmlxeaf9fuis">
              🧬 KIT MAESTRO DE BIODESCODIFICACIÓN™
            </h2>
            <div className="leaf-rule mx-auto max-w-xs" aria-hidden="true" data-id="element-160" data-mp="3vh5lsbw8lg9u" />
            
            <p className="mx-auto max-w-2xl text-[13px] font-medium leading-relaxed text-brand-cream/85 md:text-lg" data-id="element-161" data-mp="e88vpzudsc3f">
              Sistema profesional de protocolos, prácticas de integración y herramientas educativas listo para consultar desde tu próxima sesión.
            </p>
            <p className="mx-auto max-w-2xl rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-[11px] font-medium leading-relaxed text-brand-cream/75 backdrop-blur-sm md:text-sm" data-id="element-162" data-mp="3cryyscoeu2i8">
              📘  Material educativo para terapeutas y facilitadores. Herramientas de organización, exploración y acompañamiento para la práctica profesional.
            </p>
            <CTAButton variant="gold" className="cta-shimmer relative mt-2 overflow-hidden px-6 py-3 text-sm md:mt-3 md:px-10 md:py-5 md:text-xl" data-id="element-163" data-mp="2yxg6wv63ehe6">
              <span className="relative z-10" data-id="element-164" data-mp="19hm6dtslrygb">→ QUIERO CONOCER EL KIT COMPLETO</span>
            </CTAButton>
          </div>

          <div className="mx-auto mt-6 grid max-w-3xl gap-3 rounded-2xl border border-white/15 bg-white/5 p-4 backdrop-blur-sm sm:grid-cols-3 md:mt-10 md:p-5" data-id="element-165" data-mp="1eaorddjbdzkw">
            {stats.map(stat => {
            const Icon = stat.icon;
            return <div key={stat.text} className="flex flex-col items-center gap-2 text-center" data-id="element-166" data-mp="1s9ep8vdyk3ab">
                  <Icon className="h-6 w-6 text-brand-amber" data-id="element-167" data-mp="1x881kkhido8w" />
                  <span className="text-xs leading-snug text-brand-cream/80 md:text-sm" data-id="element-168" data-mp="1x7gkeeonrt25">{stat.text}</span>
                </div>;
          })}
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-2 text-[10px] font-semibold md:mt-6 md:text-sm" data-id="element-169" data-mp="1n9iulmr5ho7f">
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-170" data-mp="7s8o4r6uxcng">
              <BookOpenIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-171" data-mp="bvoi1frjkywg" />
              100% PDF
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-172" data-mp="2ejn43qs06aeq">
              <CheckCircle2Icon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-173" data-mp="29ktptw4w6fdi" />
              Garantía 7 días
            </span>
            <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-brand-cream/85" data-id="element-174" data-mp="1ztfve2rss5r8">
              <PrinterIcon className="mr-1.5 inline h-3.5 w-3.5 text-brand-amber" data-id="element-175" data-mp="1vs41hl1w4yd8" />
              También imprimible
            </span>
            <span className="rounded-full border border-brand-gold/50 bg-brand-amber/15 px-3 py-1.5 text-brand-goldLight" data-id="element-176" data-mp="1qqojk1v0f7z2">
              🌎 Disponible en toda Latinoamérica
            </span>
          </div>
        </motion.div>
      </div>
    </header>;
}